import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { HubUserSearchableMultiSelect } from "@/components/settings/HubUserSearchableMultiSelect";
import { apiFetch } from "@/lib/api";
import {
  fetchSupplierArrivalFollowers,
  replaceSupplierArrivalFollowers,
  setSelfFollowsSupplierArrival,
  supplierArrivalFollowersQueryKey,
  type SupplierArrivalFollowerRow,
} from "@/lib/masterData";
import { unorderedStringArraysEqual } from "@/lib/settingsCardDirty";
import type { NotificationTopicOwnerUserOption } from "@/components/NotificationTopicOwnersSettingsSection";

type SupplierArrivalFollowersSectionProps = {
  supplierId: number;
  canManageFollowers: boolean;
  userOptions: NotificationTopicOwnerUserOption[];
  usersLoading?: boolean;
};

function fetchStaffUserOptions(): Promise<{ users: NotificationTopicOwnerUserOption[] }> {
  return apiFetch("/api/notifications/staff-user-options");
}

export function SupplierArrivalFollowersSection({
  supplierId,
  canManageFollowers,
  userOptions: userOptionsFromParent,
  usersLoading = false,
}: SupplierArrivalFollowersSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const followersQuery = useQuery({
    queryKey: supplierArrivalFollowersQueryKey(supplierId),
    queryFn: () => fetchSupplierArrivalFollowers(supplierId),
  });

  const usersQuery = useQuery({
    queryKey: ["notifications", "staff-user-options"],
    queryFn: fetchStaffUserOptions,
    enabled: userOptionsFromParent.length === 0,
  });

  const userOptions = userOptionsFromParent.length > 0 ? userOptionsFromParent : (usersQuery.data?.users ?? []);

  useEffect(() => {
    setInitialized(false);
  }, [supplierId]);

  useEffect(() => {
    if (followersQuery.data != null && !initialized) {
      setSelectedUserIds(followersQuery.data.followers.map((row) => row.userId));
      setInitialized(true);
    }
  }, [followersQuery.data, initialized]);

  const savedUserIds = useMemo(
    () => followersQuery.data?.followers.map((row) => row.userId) ?? [],
    [followersQuery.data],
  );

  const adminDirty = canManageFollowers && !unorderedStringArraysEqual(selectedUserIds, savedUserIds);
  const currentUserFollows = followersQuery.data?.current_user_follows === true;

  const saveMutation = useMutation({
    mutationFn: () => replaceSupplierArrivalFollowers(supplierId, selectedUserIds),
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp-suppliers"] });
      await queryClient.invalidateQueries({ queryKey: supplierArrivalFollowersQueryKey(supplierId) });
      setInitialized(false);
    },
  });

  const selfFollowMutation = useMutation({
    mutationFn: (following: boolean) => setSelfFollowsSupplierArrival(supplierId, following),
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp-suppliers"] });
      await queryClient.invalidateQueries({ queryKey: supplierArrivalFollowersQueryKey(supplierId) });
      setInitialized(false);
    },
  });

  const followerLabels = (followersQuery.data?.followers ?? []).map((row: SupplierArrivalFollowerRow) => {
    const label = row.displayName?.trim() || row.email;
    return label;
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitleWithHelp
          hoverHint="Followers receive email and in-app notifications when inbound dock arrivals are logged or purchase order receipts are submitted for this supplier. Follow yourself or ask an MRP administrator to manage the list."
        >
          Inbound notifications
        </CardTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant={currentUserFollows ? "secondary" : "primary"}
            disabled={followersQuery.isLoading || selfFollowMutation.isPending}
            onClick={() => selfFollowMutation.mutate(!currentUserFollows)}
          >
            {currentUserFollows ? "Unfollow" : "Follow"}
          </Button>
          {canManageFollowers ? (
            <HubSaveButton
              type="button"
              dirty={adminDirty}
              isLoading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save followers
            </HubSaveButton>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {canManageFollowers ? (
          <HubUserSearchableMultiSelect
            id={`supplier-arrival-followers-${String(supplierId)}`}
            label="Notification followers"
            users={userOptions}
            selectedUserIds={selectedUserIds}
            onSelectedUserIdsChange={setSelectedUserIds}
            disabled={followersQuery.isLoading || usersLoading || saveMutation.isPending}
            placeholder="Search hub users…"
            noneSelectedLabel="No followers"
          />
        ) : (
          <div className="text-sm text-slate-600">
            {followerLabels.length > 0
              ? `Following: ${followerLabels.join(", ")}`
              : "No followers yet. Use Follow to receive notifications for this supplier."}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
